/**
 * Award-data SSOT — shared types + cross-cutting constants
 *
 * Phase 1.4.5 (Award Engine Integrity 2026-05-17) — Single Source of Truth
 * directory established per Option-1 decision (Path C-wide).
 *
 * ═══════════════════════════════════════════════════════════════════════
 * WHAT BELONGS HERE
 * ═══════════════════════════════════════════════════════════════════════
 *
 * - Cross-domain constants that don't fit a single per-award or per-domain
 *   file (e.g. care-minute minimums that span Aged Care Rules s.176-15 +
 *   workforce reporting + RN 24/7 calculations).
 * - Re-exports of structural interfaces that the per-award files all need
 *   (currently defined in `award-engines/recall.ts`,
 *   `award-engines/disturbance.ts`, `award-engines/shift-classifier.ts`).
 *   Provided here as a convenience indirection so per-award files have a
 *   single import path; the canonical definitions remain in the engine
 *   files until a follow-up refactor moves them.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * WHAT DOES NOT BELONG HERE
 * ═══════════════════════════════════════════════════════════════════════
 *
 * - Per-award rate / penalty / allowance / classification data — those
 *   belong in `award-data/<award>.ts` per the Option-1 isolation rule
 *   (runbook §7). Cross-award shape-porting risk is mitigated by the
 *   per-award-file isolation; sharing CONSTANTS (not just types) here
 *   would re-introduce the failure mode.
 *
 * - Per-domain regulator data (SAH program, QI thresholds, AN-ACC,
 *   residential fees, etc.) — those belong in their own per-domain files
 *   (`sah.ts`, `qi.ts`, `an-acc.ts`, `residential.ts`, `compliance.ts`).
 *
 * ═══════════════════════════════════════════════════════════════════════
 * RELATED
 * ═══════════════════════════════════════════════════════════════════════
 *
 * - Verify-first runbook: `docs/operations/runbooks/award-engine-clause-provenance.md`
 * - Cross-award shape-porting risk: `~/.claude/projects/.../memory/project_cross_award_shape_porting_risk.md`
 * - SSOT decision record: `~/.claude/projects/.../memory/project_award_engine_integrity_in_progress.md`
 *   "Phase 1.4.5 resume prompt" section.
 */

// Re-export structural interfaces so per-award files import from a single path.
// Canonical definitions remain in the engine files; this is a convenience
// indirection for the SSOT directory. Future refactor candidate (Phase 2):
// invert ownership so the interfaces live here and the engine files import them.
export type {
  RecallRules,
  RecallType,
  RecallPayBand,
  RecallPayInput,
  RecallPayBreakdown,
  RecallPayLine,
  OtRateBands,
  OtBandPair,
} from '../award-engines/recall';
export { RecallBandLookupError } from '../award-engines/recall';

export type {
  DisturbanceRules,
  DisturbanceModel,
  DisturbancePayBand,
  DisturbanceEmploymentSubtype,
  PerEventDisturbanceInput,
  LesserOfWindowDisturbanceInput,
  DisturbancePayInput,
  DisturbancePayBreakdown,
  DisturbancePayOptions,
} from '../award-engines/disturbance';

export type { ShiftCharacterRules, DayType } from '../award-engines/shift-classifier';

/**
 * AN-ACC mandatory care minutes (from 1 October 2024).
 *
 * Total: 215 minutes per resident per day.
 * RN: 44 minutes per resident per day.
 *
 * Cross-cutting because it spans:
 *   - Aged Care Rules 2025 s.176-15 (care time obligation; legislative basis)
 *   - DoH Care Minutes Guide v2.0 (operational measurement)
 *   - Aged Care Award MA000018 + Nurses Award MA000034 (worker classification
 *     attribution; see project_care_minutes_attribution_model.md memory)
 *   - QFR reporting (Quarterly Financial Report → DoH dashboard substitution)
 *
 * Source: Aged Care Rules 2025 (F2025L01173) s.176-15 + DoH Care Minutes
 * Guide v2.0 (11 Dec 2025) §3.1.2 / §5.5.1 / Appendix 2.
 * Verified: 2026-05-17 by aged-care-legislation specialist (Phase 1.4 Cluster E2).
 * Artifact: docs/operations/clause-verifications/2026-05-17-aged-care-legislation-acqsc-care-minutes-attribution.md
 * Next review: when DoH publishes a v2.1+ Care Minutes Guide OR when
 * Aged Care Rules 2025 s.176-15 amendment is gazetted.
 */
export const CARE_MINUTE_MINIMUMS = {
  totalMinutesPerResidentPerDay: 215,
  rnMinutesPerResidentPerDay: 44,
} as const;
